import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useProject } from '../../project-hooks/useProject';
import { getPublishedRewardsForProject } from '../../projects-reducer';
import {
    eligibleToShowAdsenseAds,
    getSupportLink,
    isActive,
    isPublished,
    replaceProjectTemplateTextsWithPlaceHolders,
} from '../../project-model';
import { ContentTitle, Lead } from '../../../components/typography/Heading';
import DescriptionWrapper from '../../../components/description-wrapper/DescriptionWrapper';
import RewardList from '../../reward/RewardList';
import UpdateNotification from '../../project-update/update-notification/UpdateNotification';
import { trackEvent } from '../../../support-and-tracking/analytics-actions';
import CollapseChildren from '../../../components/collapse-children/CollapseChildren';
import PublicSubprojectDisplay from '../../subproject/PublicSubprojectDisplay/PublicSubprojectDisplay';
import { ActionButton } from '@sb1/ffe-buttons-react';
import { ProjectTagInfo } from '../tag-info/ProjectTagInfo';
import { useNavigate } from 'react-router';
import ProjectPolls from '../project-polls/ProjectPolls';
import styles from './ProjectMain.module.less';
import { useI18nTexts } from '../../i18n/useI18nTexts';
import {
    HIDE_DESCRIPTION_TEXT_KEY,
    SHOW_DESCRIPTION_TEXT_KEY,
    SUPPORT_PROJECT_LONG_TEXT_KEY,
} from '../../i18n/project-i18n-model';
import { GoogleAdsense } from '../../../components/ads/GoogleAdsense';
import { PROJECT_SHOW_ADSENSE } from '../../../constants/features/constants';
import { useFeatureVariant } from '../../../components/hooks/isFeatureEnabled';

const ProjectMain = () => {
    const project = useProject();
    const navigate = useNavigate();
    const publishedRewards = useSelector((state) => getPublishedRewardsForProject(state, project.id));
    const rewards = useSelector((state) => state.projects.rewards?.[project.id]) || [];
    const userAgent = useSelector((state) => state.userAgent);
    const isProjectPublished = isPublished(project);
    const projectRewards = isProjectPublished ? publishedRewards : rewards;
    const showAdSense =
        useFeatureVariant(PROJECT_SHOW_ADSENSE, 'test') && eligibleToShowAdsenseAds({ project, userAgent });

    const texts = useI18nTexts([HIDE_DESCRIPTION_TEXT_KEY, SHOW_DESCRIPTION_TEXT_KEY, SUPPORT_PROJECT_LONG_TEXT_KEY]);

    const [title, setTitle] = useState(project?.title);
    const [purpose, setPurpose] = useState(project?.purpose);
    useEffect(() => {
        if (project?.title) {
            setTitle(replaceProjectTemplateTextsWithPlaceHolders(project.title, project));
        }
        if (project?.purpose) {
            setPurpose(replaceProjectTemplateTextsWithPlaceHolders(project.purpose, project));
        }
    }, [project?.id]);

    const collapseButtonTracking = useCallback(
        () =>
            trackEvent('project-info', 'collapse', {
                'project-id': project.id,
            }),
        [project?.id]
    );
    const expandButtonTracking = useCallback(() => {
        trackEvent('project-info', 'expand', {
            'project-id': project.id,
        });
    }, [project?.id]);

    if (!project) {
        return null;
    }

    const navigateToPayment = () => {
        const supportLink = getSupportLink({ projectId: project.id, rewards: publishedRewards });
        navigate(supportLink);
    };

    return (
        <div className={styles.projectMain}>
            <UpdateNotification className={styles.latestUpdatewef} />

            {showAdSense && <GoogleAdsense dataFullWidthResponsive={true} dataAdSlot="2973080449" />}

            <ProjectPolls />

            <article className={styles.projectFields}>
                <ContentTitle tag={'h1'} className={styles.projectTitle}>
                    {title}
                </ContentTitle>

                <CollapseChildren
                    collapseButtonText={texts[HIDE_DESCRIPTION_TEXT_KEY]}
                    expandButtonText={texts[SHOW_DESCRIPTION_TEXT_KEY]}
                    expandButtonTracking={expandButtonTracking}
                    collapseButtonTracking={collapseButtonTracking}
                >
                    <Lead className={styles.purpose}>{purpose}</Lead>
                    <DescriptionWrapper>
                        <div className={styles.description} dangerouslySetInnerHTML={project.descriptionHtml} />
                    </DescriptionWrapper>
                </CollapseChildren>

                {!project?.profile?.hideProjectTagLinks && <ProjectTagInfo tags={project.tags} />}

                {projectRewards?.length === 0 && isActive(project) && (
                    <ActionButton
                        className={styles.supportButton}
                        onClick={() => {
                            trackEvent('support', 'open', {
                                'button-text': texts[SUPPORT_PROJECT_LONG_TEXT_KEY],
                                buttonType: 'description',
                            });
                            navigateToPayment();
                        }}
                    >
                        {texts[SUPPORT_PROJECT_LONG_TEXT_KEY]}
                    </ActionButton>
                )}
            </article>

            {project.subprojects_enabled && <PublicSubprojectDisplay projectId={project.id} />}

            <RewardList rewards={projectRewards} />
        </div>
    );
};
export default ProjectMain;
